import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getConfig } from '@edx/frontend-platform';
import { getAuthService } from '@edx/frontend-platform/auth';
import { useIntl } from '@edx/frontend-platform/i18n';

import { windowScrollTo } from '../../../utils';
import { Notifications } from '../components';
import type { LoginFailureMessageProps, NotificationVariantTypes } from './types';
import {
  ACCOUNT_LOCKED_OUT,
  ALLOWED_DOMAIN_LOGIN_ERROR,
  FAILED_LOGIN_ATTEMPT,
  FORBIDDEN_REQUEST,
  INACTIVE_USER,
  INCORRECT_EMAIL_PASSWORD,
  INTERNAL_SERVER_ERROR,
  INVALID_FORM,
  NON_COMPLIANT_PASSWORD_EXCEPTION,
  NUDGE_PASSWORD_CHANGE,
  PASSWORD_RESET_INVALID_TOKEN,
  PASSWORD_RESET_SUCCESS,
  FORGOT_PASSWORD_SUCCESS,
  REQUIRE_PASSWORD_CHANGE,
  TPA_AUTHENTICATION_FAILURE,
  NOTIFICATION_TYPES,
} from './constants';

import messages from './messages';

const LoginNotification = (
  { context, code, count }: LoginFailureMessageProps,
): React.ReactElement | null => {
  const intl = useIntl();
  const authService = getAuthService();

  useEffect(() => {
    windowScrollTo({ left: 0, top: 0, behavior: 'smooth' });
  }, [code, count]);

  if (!code) {
    return null;
  }

  let resetLink = (
    <Link to="/forgot-password">
      {intl.formatMessage(messages.resetPassword)}
    </Link>
  );

  const emailStrong = (chunks) => <strong>{chunks}</strong>;

  let message: React.ReactNode = '';
  let title: string = intl.formatMessage(messages.couldNotSignIn);
  let variant: NotificationVariantTypes = NOTIFICATION_TYPES.ERROR;

  switch (code) {
    case NON_COMPLIANT_PASSWORD_EXCEPTION: {
      message = (
        <>
          <strong>{intl.formatMessage(messages.passwordRequirementsChanged)}</strong>
          <p>
            {intl.formatMessage(messages.nonCompliantPassword)}
          </p>
        </>
      );
      break;
    }
    case FORBIDDEN_REQUEST:
      message = <p>{intl.formatMessage(messages.tooManyAttempts)}</p>;
      break;
    case INACTIVE_USER: {
      const supportLink = (
        <Link to="/contact">
          {intl.formatMessage(messages.contactSupport, { platformName: context?.platformName })}
        </Link>
      );
      message = (
        <p>
          {intl.formatMessage(messages.activateAccount)}
          <br /><br />
          {intl.formatMessage(messages.activationLinkSent, { email: context?.email, strong: emailStrong })}
          {' '}
          {supportLink}.
        </p>
      );
      break;
    }
    case ALLOWED_DOMAIN_LOGIN_ERROR: {
      message = (
        <p>
          {intl.formatMessage(messages.allowedDomainLogin, {
            allowedDomain: context?.allowedDomain,
            provider: context?.provider,
          })}
        </p>
      );
      break;
    }
    case INVALID_FORM:
      message = <p>{intl.formatMessage(messages.fillFields)}</p>;
      break;
    case FAILED_LOGIN_ATTEMPT: {
      resetLink = (
        <Link to="/forgot-password">
          {intl.formatMessage(messages.clickHereToReset)}
        </Link>
      );
      message = (
        <>
          <p>
            {intl.formatMessage(
              messages.incorrectCredentialsAttempts,
              { remainingAttempts: context?.remainingAttempts },
            )}
          </p>
          <p>
            {intl.formatMessage(messages.forgotPasswordPrompt)} {resetLink}
          </p>
        </>
      );
      break;
    }
    case ACCOUNT_LOCKED_OUT: {
      message = (
        <>
          <p>{intl.formatMessage(messages.accountLocked)}</p>
          <p>
            {intl.formatMessage(messages.resetBeforeRetry)} {resetLink}
          </p>
        </>
      );
      break;
    }
    case INCORRECT_EMAIL_PASSWORD:
      if (context?.failure_count && context?.failure_count < 2) {
        message = <p>{intl.formatMessage(messages.incorrectCredentials)}</p>;
      } else {
        message = (
          <p>
            {intl.formatMessage(messages.incorrectCredentialsOrReset)} {resetLink}.
          </p>
        );
      }
      break;
    case NUDGE_PASSWORD_CHANGE:
      // Need to clear the CSRF token here to fetch a new one because token is already rotated after successful login.
      if (authService) {
        authService.getCsrfTokenService().clearCsrfTokenCache();
      }
      message = (
        <>
          <p>
            {intl.formatMessage(messages.vulnerablePassword)}
          </p>
          <p>
            <Link to="/forgot-password">
              {intl.formatMessage(messages.resetPassword)}
            </Link>
          </p>
        </>
      );
      break;
    case REQUIRE_PASSWORD_CHANGE:
      message = (
        <>
          <p>
            {intl.formatMessage(messages.vulnerablePasswordChange)}
          </p>
          <p>
            <Link to="/forgot-password">
              {intl.formatMessage(messages.resetPassword)}
            </Link>
          </p>
        </>
      );
      break;
    case TPA_AUTHENTICATION_FAILURE:
      message = (
        <p>
          {intl.formatMessage(messages.tpaAuthFailure, { siteName: getConfig().SITE_NAME })}
          <br /><br />
          {intl.formatMessage(messages.errorDetails)}
          <br />
          {context?.errorMessage}
        </p>
      );
      break;
    case PASSWORD_RESET_INVALID_TOKEN:
      title = intl.formatMessage(messages.invalidResetLinkTitle);
      message = (
        <p>
          {intl.formatMessage(messages.invalidResetLinkText)}
        </p>
      );
      break;
    case PASSWORD_RESET_SUCCESS:
      variant = NOTIFICATION_TYPES.SUCCESS;
      title = intl.formatMessage(messages.successTitle);
      message = (
        <p>
          {intl.formatMessage(messages.successDescription)}
        </p>
      );
      break;
    case FORGOT_PASSWORD_SUCCESS:
      variant = NOTIFICATION_TYPES.SUCCESS;
      title = intl.formatMessage(messages.forgotPasswordSuccessTitle);
      message = (
        <p>
          {intl.formatMessage(messages.forgotPasswordSuccessDescription)}
        </p>
      );
      break;
    case INTERNAL_SERVER_ERROR:
    default:
      message = <p>{intl.formatMessage(messages.genericError)}</p>;
      break;
  }

  return (
    <Notifications
      title={title}
      variant={variant}
      message={message}
      count={count}
    />
  );
};

export default LoginNotification;
